import {
  ArrowDownRight,
  ArrowUpRight,
  DollarSign,
  Package,
  ShoppingCart,
  UserCheck,
  UserX,
  Users,
} from "lucide-react";
import React from "react";

import { Card, CardContent } from "@/components/ui";
import type { AdminDashboardData } from "@/hooks/useAdminDashboard";
import { fmtVal } from "@/lib/orders";
import { cn } from "@/lib/utils";

import { iconBg } from "./sellerDetail.constants";

interface SellersKpiCardsProps {
  dash: AdminDashboardData | null;
  dashLoading: boolean;
}

export const SellersKpiCards: React.FC<SellersKpiCardsProps> = ({
  dash,
  dashLoading,
}) => {
  const kpiCards = [
    {
      key: "totalSellers",
      label: "Total Sellers",
      icon: Users,
      color: "indigo",
      value: dash?.sellers.totalCount ?? 0,
      delta: dash?.sellers.dayOnDayChangePct ?? 0,
      format: "number",
    },
    {
      key: "activeSellers",
      label: "Active Sellers",
      icon: UserCheck,
      color: "emerald",
      value: dash?.sellers.activeCount ?? 0,
      delta: dash?.sellers.dayOnDayChangePct ?? 0,
      format: "number",
    },
    {
      key: "blockedSellers",
      label: "Blocked Sellers",
      icon: UserX,
      color: "rose",
      value: dash?.sellers.suspendedCount ?? 0,
      delta: 0,
      format: "number",
    },
    {
      key: "totalActiveOrders",
      label: "Total Active Orders",
      icon: ShoppingCart,
      color: "amber",
      value: dash?.orders.pendingCount ?? 0,
      delta: dash?.orders.dayOnDayChangePct ?? 0,
      format: "number",
    },
    {
      key: "totalProducts",
      label: "Total Uploaded Products",
      icon: Package,
      color: "blue",
      value: dash?.listings.totalCount ?? 0,
      delta: dash?.listings.dayOnDayChangePct ?? 0,
      format: "number",
    },
    {
      key: "revenue7d",
      label: "Revenue (Last 7 Days)",
      icon: DollarSign,
      color: "teal",
      value: dash?.orders.sevenDayRevenue ?? 0,
      delta: dash?.orders.dayOnDayRevenueChangePct ?? 0,
      format: "currency",
    },
  ] as const;

  return (
    <div
      data-testid="sellers-kpi-grid"
      className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8"
    >
      {kpiCards.map((kpi) => {
        const Icon = kpi.icon;
        const isUp = kpi.delta >= 0;
        const displayValue =
          kpi.format === "currency"
            ? fmtVal(kpi.value, "currency")
            : kpi.value.toLocaleString();

        return (
          <Card key={kpi.key} data-testid={`kpi-${kpi.key}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={cn("p-2 rounded-lg", iconBg[kpi.color])}>
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <span
                  className={cn(
                    "inline-flex items-center gap-0.5 text-xs font-medium",
                    isUp
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-rose-600 dark:text-rose-400",
                  )}
                >
                  {isUp ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3" />
                  )}
                  {`${isUp ? "+" : ""}${kpi.delta.toFixed(1)}%`}
                </span>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {dashLoading ? "—" : displayValue}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {kpi.label}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
